import { useEffect, useState } from 'react';
import { AlertTriangle, MapPin, Phone } from 'lucide-react';
import { apiClient } from '../api/client';
import { useTechnicians } from '../hooks/useTechnicians';
import { useBoardUpdates } from '../hooks/useBoardUpdates';
import { ORDER_STATUS_LABELS, type GpsPosition, type Order } from '../types';
import TechnicianAvatar from './TechnicianAvatar';

const TERMINAL_STATUSES = ['COMPLETED', 'CANCELLED'];
const ACTIVE_WORK_STATUSES = ['EN_ROUTE', 'ARRIVED', 'IN_PROGRESS'];
// То же окно, что и в бэкендовом getDashboardStats() — "на смене" значит координаты
// приходили за последние 20 минут, а не просто "есть активный наряд"
const ON_SHIFT_WINDOW_MS = 20 * 60 * 1000;

interface MapSidePanelProps {
  onOrderClick?: (orderId: string) => void;
}

// Правая панель карты (screen 01 макета) — вместо общего списка нарядов показывает то, что
// диспетчеру реально нужно на карте: что горит прямо сейчас + кто на смене и чем занят
export default function MapSidePanel({ onOrderClick }: MapSidePanelProps) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [positions, setPositions] = useState<GpsPosition[]>([]);
  const technicians = useTechnicians();

  function load() {
    apiClient
      .get<Order[]>('/orders', { params: { status: 'active' } })
      .then(({ data }) => setOrders(data))
      .catch((err) => console.error('Не удалось загрузить наряды', err));
    apiClient
      .get<GpsPosition[]>('/tracking/positions')
      .then(({ data }) => setPositions(data))
      .catch((err) => console.error('Не удалось загрузить позиции сантехников', err));
  }

  useEffect(() => {
    load();
    const interval = setInterval(load, 15000);
    return () => clearInterval(interval);
  }, []);
  useBoardUpdates(['orders'], load);

  const lastSeenByTechnician = new Map<string, number>();
  for (const p of positions) {
    const t = new Date(p.recordedAt).getTime();
    const prev = lastSeenByTechnician.get(p.technicianId);
    if (!prev || t > prev) lastSeenByTechnician.set(p.technicianId, t);
  }
  const onShift = (technicianId: string) => {
    const seen = lastSeenByTechnician.get(technicianId);
    return !!seen && Date.now() - seen < ON_SHIFT_WINDOW_MS;
  };

  const urgent = orders
    .filter((o) => !TERMINAL_STATUSES.includes(o.status))
    .filter((o) => !o.technicianId || (o.scheduledAt && new Date(o.scheduledAt).getTime() < Date.now()))
    .sort((a, b) => {
      const ta = a.scheduledAt ? new Date(a.scheduledAt).getTime() : Infinity;
      const tb = b.scheduledAt ? new Date(b.scheduledAt).getTime() : Infinity;
      return ta - tb;
    });

  const activeTechnicians = technicians.filter((t) => t.isActive !== false);

  return (
    <div className="map-side-panel">
      <div className="map-side-section">
        <div className="order-detail-label">Требуют внимания</div>
        {urgent.length === 0 && <p className="muted">Ничего срочного нет</p>}
        <div className="map-urgent-list">
          {urgent.map((o) => {
            const overdue = !!o.scheduledAt && new Date(o.scheduledAt).getTime() < Date.now();
            return (
              <button key={o.id} className="map-urgent-row" onClick={() => onOrderClick?.(o.id)}>
                <div className="map-urgent-row-top">
                  <span className={`status-badge status-${o.status}`}>{ORDER_STATUS_LABELS[o.status]}</span>
                  {overdue && (
                    <span className="map-urgent-flag">
                      <AlertTriangle size={11} /> Просрочен
                    </span>
                  )}
                  {!o.technicianId && <span className="map-urgent-flag">Без мастера</span>}
                </div>
                <div className="map-urgent-row-address">
                  <MapPin size={12} className="order-row-icon" />
                  {o.address}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="map-side-section">
        <div className="order-detail-label">Мастера на смене</div>
        <div className="map-tech-list">
          {activeTechnicians.map((t) => {
            const active = onShift(t.id);
            const currentOrder = orders.find(
              (o) => o.technicianId === t.id && ACTIVE_WORK_STATUSES.includes(o.status),
            );
            return (
              <div key={t.id} className={`map-tech-row ${active ? '' : 'map-tech-row-offline'}`}>
                <TechnicianAvatar id={t.id} name={t.fullName} size={30} />
                <div className="map-tech-row-info">
                  <div className="map-tech-row-name">{t.fullName}</div>
                  <div className="muted">
                    {currentOrder ? ORDER_STATUS_LABELS[currentOrder.status] : active ? 'Свободен' : 'Не на смене'}
                  </div>
                </div>
                <div className="map-tech-row-actions">
                  <a href={`tel:${t.phone}`} title="Позвонить">
                    <Phone size={14} />
                  </a>
                  {currentOrder && (
                    <button className="btn-link" onClick={() => onOrderClick?.(currentOrder.id)}>
                      Наряд
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
